// 페이지 가이드 투어 공통 스텝 빌더.
// 각 tours/<key>Tour.js 는 specs 배열(아래 형태)만 정의하고 buildSteps 로 Shepherd 스텝으로 변환한다.
//
// spec = {
//   id: string,
//   emoji?: string,          // 제목 앞 이모지
//   title: string,
//   text: string,            // 본문(HTML 가능)
//   attachTo?: { element: string(셀렉터), on: 'top'|'bottom'|'left'|'right' },  // 없으면 중앙(환영/완료) 스텝
//   beforeShow?: string,     // 표시 전 대기할 셀렉터(지연 렌더 대비)
// }
// falsy 항목(조건부 `cond && {...}`)은 자동 제외된다.

// 셀렉터가 나타날 때까지 대기 후, 타깃을 화면 중앙으로 스크롤하고 resolve.
// (없으면 timeout 후 진행 → Shepherd 센터 폴백)
// ※ 표시 전에 스크롤해둬야 floating-ui 가 올바른 위치를 계산함. 어드민은 .contents 컨테이너가
//    스크롤되는데 smooth scrollIntoView 가 동작하지 않아 instant(block:center)로 처리.
export function waitFor(selector, timeout = 1500) {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = selector && document.querySelector(selector);
      if (el) {
        try {
          const r = el.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          // 화면 밖(위/아래로 벗어남)일 때만 중앙 스크롤 — 이미 보이면 불필요한 스크롤 안 함.
          if (r.top < 0 || r.bottom > vh) el.scrollIntoView({ block: 'center' });
        } catch (e) {}
        return resolve();
      }
      if (Date.now() - start > timeout) return resolve();
      setTimeout(tick, 100);
    };
    tick();
  });
}

// 단일 spec → Shepherd step 옵션 (버튼/진행표시/중앙·부착 처리 공통화)
function toStep(spec, index, total, tour) {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const buttons = [];
  buttons.push({ text: '건너뛰기', action: () => tour.cancel(), classes: 'orp-guide__btn orp-guide__btn--ghost' });
  if (!isFirst) buttons.push({ text: '이전', action: () => tour.back(), classes: 'orp-guide__btn orp-guide__btn--ghost' });
  buttons.push(
    isLast
      ? { text: '완료', action: () => tour.complete(), classes: 'orp-guide__btn orp-guide__btn--primary' }
      : { text: '다음', action: () => tour.next(), classes: 'orp-guide__btn orp-guide__btn--primary' }
  );

  const step = {
    id: spec.id,
    title: (spec.emoji ? spec.emoji + ' ' : '') + spec.title,
    text:
      '<div class="orp-guide__body">' + spec.text + '</div>' +
      '<div class="orp-guide__progress">' + (index + 1) + ' / ' + total + '</div>',
    buttons,
  };

  if (spec.attachTo) {
    step.attachTo = spec.attachTo;
    // 모든 부착 스텝: 표시 전 타깃 대기 + 화면 안으로 스크롤(waitFor) → 말풍선이 화면 안 올바른 위치에.
    step.beforeShowPromise = () => waitFor(spec.beforeShow || (spec.attachTo && spec.attachTo.element));
  } else {
    step.classes = 'orp-guide orp-guide--center';   // 환영/완료 등 중앙 스텝
  }

  return step;
}

// specs 배열(falsy 제외) → Shepherd 스텝 배열. 진행표시 총개수는 제외 후 길이 기준.
export function buildSteps(specs, tour) {
  const list = (specs || []).filter(Boolean);
  const total = list.length;
  return list.map((spec, i) => toStep(spec, i, total, tour));
}

export default buildSteps;
